import Slideshow from 'components/Slideshow';
import SlideshowInterface from 'types/Slideshow';
import Layout from 'components/Layout';
import BackSubNav from 'components/BackSubNav';
import Head from 'next/head';
import * as _ from 'lodash-es';
import fetchSlideshow from 'lib/UseCases/FetchSlideshowUseCase';
import Error from 'pages/_error';
import { SLIDESHOW_URL } from 'lib/ContentGroups';
import { useTranslation } from 'react-i18next';
import StructuredData from 'components/StructuredData';
import SlideshowStructuredData from 'lib/StructuredData/Slideshow';
import captureException from 'lib/captureException';
import useAdSetup from 'lib/hooks/useAdSetup';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import breadcrumbStructuredData from 'lib/StructuredData/breadcrumbStructuredData';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import config from 'lib/config';
import { fetchSearchUrl } from 'lib/AutolistAPI';
import { AdInfo } from 'lib/CarGurusAds';
import { enableContentCaching } from 'lib/cacheControl';
import isIndexable from 'lib/isIndexable';

interface SlideshowPageInitialProps {
  slideshow?: SlideshowInterface;
  shareUrl?: string;
  statusCode?: number;
  slug?: string;
  contentGroups?: { default: string };
  carGurusAdInfo?: AdInfo | null;
  indexable: boolean;
}

export default function SlideshowPage({
  slideshow,
  statusCode,
  slug,
  carGurusAdInfo,
  indexable,
}: SlideshowPageInitialProps): JSX.Element {
  useContentGroups({ default: SLIDESHOW_URL });
  const { t } = useTranslation();

  let carGurusAdTargeting: AdInfo = {
    ad_unit: '/19485787/AutoList.com/Research',
  };

  if (carGurusAdInfo) {
    carGurusAdTargeting = { ...carGurusAdInfo };
  }

  useAdSetup(carGurusAdTargeting.ad_unit as string, {
    make: carGurusAdTargeting.make,
    model: carGurusAdTargeting.model,
    year: carGurusAdTargeting.year,
    pageType: carGurusAdTargeting.page_type,
    ageType: carGurusAdTargeting.age_type,
    cpo: carGurusAdTargeting.cpo,
    inLine: carGurusAdTargeting.in_line,
    body_style: carGurusAdTargeting.body_style,
    category: carGurusAdTargeting.category,
    app: carGurusAdTargeting.app,
    sellerId: carGurusAdTargeting.seller_id,
  });

  if (!slideshow || (statusCode && statusCode !== 200)) {
    return <Error statusCode={statusCode} />;
  }

  const slideshowUrl = `${config.host}/gallery/${slug}`;

  const structuredData = new SlideshowStructuredData({
    ...slideshow,
    ...{ slideshowUrl },
  });

  return (
    <>
      <Layout
        title={slideshow.title}
        description={slideshow.metaDescription}
        subNav={<BackSubNav url={'/guides'} title={t('guides.guides')} />}
        includeAds={false}
        indexed={indexable}
      >
        <Head>
          <meta content={slideshow.coverSlide.photoUrl} property="og:image" key="og:image" />
          <meta content="article" property="og:type" key="og:type" />
          <link rel="canonical" href={slideshowUrl} />
        </Head>
        <Slideshow slideshow={slideshow} />
      </Layout>
      <StructuredData data={structuredData.toJSON()} />
      <StructuredData
        data={breadcrumbStructuredData({
          url: slideshowUrl,
          title: slideshow.title,
          breadcrumbCategory: 'guides',
        })}
      />
    </>
  );
}

export async function getServerSideProps({
  query,
  req,
  res,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<SlideshowPageInitialProps>> {
  const bypassCache = true;
  const indexable = isIndexable(req);
  let fetchedSlideshow;
  let slideshow;
  let slug;

  try {
    fetchedSlideshow = await fetchSlideshow({ slug: String(query.slug) }, { bypassCache });
    slideshow = fetchedSlideshow;
    if (fetchedSlideshow) {
      slug = fetchedSlideshow.searchUrlSlug;
    }
  } catch (error) {
    // When there is an api error, we don't want the page to 500 because we
    // want graceful error handling on Autolist-web, the api has its own error
    // monitoring
    captureException(error);
  }

  if (!slideshow || _.isEmpty(slideshow)) {
    res.statusCode = 404;
    return {
      props: {
        statusCode: res.statusCode,
        indexable,
      },
    };
  }

  let searchUrl;
  try {
    searchUrl = await fetchSearchUrl(slug as string, { ads: 'web' });
  } catch (e) {
    captureException(e);
  }

  let carGurusAdInfo;
  if (searchUrl) {
    carGurusAdInfo = searchUrl.data.attributes.ad_info;
  }

  enableContentCaching(res);

  return {
    props: {
      slug: String(query.slug),
      slideshow,
      statusCode: res.statusCode,
      carGurusAdInfo: carGurusAdInfo ?? null,
      indexable,
    },
  };
}
